<template>
  <main class="adm-page">

    <div class="adm-header">
      <div><h1 class="adm-header__title">Gestion des Projets</h1><p class="adm-header__sub">{{ projectsPagination.total }} projets</p></div>
      <div class="adm-header__actions">
        <button class="adm-btn adm-btn--terra" @click="openNewProjectDialog"><Plus />Nouveau projet</button>
      </div>
    </div>

    <!-- KPIs -->
    <div class="adm-kpis">
      <div class="adm-kpi adm-kpi--forest"><div class="adm-kpi__icon"><Folder /></div><span class="adm-kpi__val">{{ projectStats.total||0 }}</span><span class="adm-kpi__lbl">Total projets</span></div>
      <div class="adm-kpi adm-kpi--sage"><div class="adm-kpi__icon"><TrendingUp /></div><span class="adm-kpi__val">{{ projectStats.actifs||0 }}</span><span class="adm-kpi__lbl">Actifs</span></div>
      <div class="adm-kpi adm-kpi--amber"><div class="adm-kpi__icon"><Users /></div><span class="adm-kpi__val">{{ formatNumber(projectStats.beneficiaires_total||0) }}</span><span class="adm-kpi__lbl">Bénéficiaires</span></div>
      <div class="adm-kpi adm-kpi--terra"><div class="adm-kpi__icon"><DollarSign /></div><span class="adm-kpi__val">{{ formatAmount(projectStats.budget_total||0) }}</span><span class="adm-kpi__lbl">Budget total</span></div>
    </div>

    <!-- Filtres -->
    <div class="adm-filters">
      <div class="adm-search" style="flex:1;min-width:200px"><Search /><input v-model="filters.search" @input="debouncedSearch" placeholder="Rechercher…" class="adm-input" /></div>
      <select v-model="filters.status" @change="applyFilters" class="adm-input adm-input--sm">
        <option value="">Tous statuts</option><option value="planifié">Planifié</option><option value="actif">Actif</option><option value="terminé">Terminé</option><option value="suspendu">Suspendu</option>
      </select>
      <select v-model="filters.category" @change="applyFilters" class="adm-input adm-input--sm">
        <option value="">Toutes catégories</option><option value="Infrastructure">Infrastructure</option><option value="Formation">Formation</option><option value="Environnement">Environnement</option>
      </select>
      <button class="adm-btn adm-btn--outline adm-btn--sm" @click="resetFilters"><X />Réinitialiser</button>
    </div>

    <!-- Table -->
    <div class="adm-card">
      <div v-if="loading" class="adm-loading"><span class="adm-ring"></span></div>
      <div v-else>
        <div class="adm-table-wrap">
          <table class="adm-table">
            <thead>
              <tr>
                <th>Projet</th><th>Catégorie</th><th>Progression</th>
                <th>Bénéficiaires</th><th>Budget</th><th>Dates</th><th>Statut</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!projects.length"><td colspan="8" class="adm-table__empty">Aucun projet trouvé</td></tr>
              <tr v-for="p in projects" :key="p.id">
                <td data-label="Projet">
                  <div style="display:flex;align-items:center;gap:.65rem">
                    <div class="adm-thumb">
                      <img v-if="p.featured_image" :src="imageUrl+p.featured_image" :alt="p.title" /><Folder v-else />
                    </div>
                    <div><div class="adm-cell-title">{{ p.title }}</div><div class="adm-cell-sub">{{ p.location }}</div></div>
                  </div>
                </td>
                <td data-label="Catégorie"><span class="adm-chip">{{ p.category }}</span></td>
                <td data-label="Progression">
                  <div style="display:flex;flex-direction:column;gap:.3rem">
                    <span style="font-size:.78rem;font-weight:600;color:var(--forest)">{{ p.progress || 0 }}%</span>
                    <div class="adm-progress-track" style="width:100px"><div class="adm-progress-fill" :style="{width:(p.progress||0)+'%'}"></div></div>
                  </div>
                </td>
                <td data-label="Bénéficiaires" class="adm-cell-num">{{ formatNumber(p.beneficiaries||0) }}</td>
                <td data-label="Budget">{{ formatAmount(p.budget||0) }}</td>
                <td data-label="Dates" class="adm-cell-date">
                  <div>{{ fmtDate(p.start_date) }}</div>
                  <div style="color:var(--ink-60);font-size:.75rem">→ {{ fmtDate(p.end_date) }}</div>
                </td>
                <td data-label="Statut">
                  <span class="adm-badge" :class="{
                    'adm-badge--sage':  p.status==='actif',
                    'adm-badge--amber': p.status==='planifié',
                    'adm-badge--green': p.status==='terminé',
                    'adm-badge--gray':  p.status==='suspendu'
                  }">{{ p.status }}</span>
                </td>
                <td class="no-label">
                  <div class="adm-actions">
                    <div class="adm-drop">
                      <button class="adm-btn adm-btn--outline adm-btn--xs" @click.stop="toggleActionsMenu(p.id)"><MoreVertical /></button>
                      <div v-if="activeActionMenu===p.id" class="adm-drop__menu">
                        <button class="adm-drop__item" @click="editProject(p)"><Edit />Modifier</button>
                        <button class="adm-drop__item" @click="openProgressModal(p)"><TrendingUp />Progression</button>
                        <button class="adm-drop__item adm-drop__item--red" @click="confirmDelete(p)"><Trash2 />Supprimer</button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="projectsPagination.last_page > 1" class="adm-pagination">
          <p class="adm-pagination__info">{{ projectsPagination.total }} projets</p>
          <div class="adm-pagination__pages">
            <button class="adm-page-btn" :disabled="projectsPagination.current_page===1" @click="changePage(projectsPagination.current_page-1)"><ChevronLeft /></button>
            <button v-for="pg in displayedPages" :key="pg" class="adm-page-btn" :class="{'adm-page-btn--active':projectsPagination.current_page===pg}" @click="changePage(pg)">{{ pg }}</button>
            <button class="adm-page-btn" :disabled="projectsPagination.current_page===projectsPagination.last_page" @click="changePage(projectsPagination.current_page+1)"><ChevronRight /></button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ MODAL Projet ════════════════════════════ -->
    <Transition name="adm-modal">
      <div v-if="showDialog" class="adm-modal" @click.self="closeDialog">
        <div class="adm-modal__box adm-modal__box--lg">
          <div class="adm-modal__head"><h3 class="adm-modal__title">{{ editingProject?'Modifier le projet':'Nouveau projet' }}</h3><button class="adm-modal__close" @click="closeDialog"><X /></button></div>
          <form @submit.prevent="submitForm" class="adm-modal__body">
            <div class="adm-field"><label class="adm-label">Titre <span>*</span></label><input v-model="projectForm.title" required class="adm-input" /></div>
            <div class="adm-form-grid">
              <div class="adm-field"><label class="adm-label">Catégorie <span>*</span></label>
                <select v-model="projectForm.category" required class="adm-input">
                  <option value="">Sélectionner…</option><option value="Infrastructure">Infrastructure</option><option value="Formation">Formation</option><option value="Environnement">Environnement</option>
                </select>
              </div>
              <div class="adm-field"><label class="adm-label">Statut</label>
                <select v-model="projectForm.status" class="adm-input">
                  <option value="planifié">Planifié</option><option value="actif">Actif</option><option value="terminé">Terminé</option><option value="suspendu">Suspendu</option>
                </select>
              </div>
              <div class="adm-field"><label class="adm-label">Début</label><input v-model="projectForm.start_date" type="date" class="adm-input" /></div>
              <div class="adm-field"><label class="adm-label">Fin prévue</label><input v-model="projectForm.end_date" type="date" class="adm-input" /></div>
              <div class="adm-field"><label class="adm-label">Lieu</label><input v-model="projectForm.location" class="adm-input" /></div>
              <div class="adm-field"><label class="adm-label">Bénéficiaires</label><input v-model.number="projectForm.beneficiaries" type="number" min="0" class="adm-input" /></div>
              <div class="adm-field"><label class="adm-label">Budget (EUR)</label><input v-model.number="projectForm.budget" type="number" min="0" class="adm-input" /></div>
              <div class="adm-field"><label class="adm-label">Progression (%)</label><input v-model.number="projectForm.progress" type="number" min="0" max="100" class="adm-input" /></div>
            </div>
            <div class="adm-field"><label class="adm-label">Description</label><textarea v-model="projectForm.description" rows="3" class="adm-input adm-textarea"></textarea></div>
            <div class="adm-field"><label class="adm-label">Image principale</label>
              <input ref="projImgInput" type="file" accept="image/*" @change="handleImageUpload" style="display:none" />
              <div v-if="projectForm.featured_image_preview||projectForm.featured_image" class="adm-upload-preview">
                <img :src="projectForm.featured_image_preview||imageUrl+projectForm.featured_image" />
                <button type="button" @click="projectForm.featured_image=null;projectForm.featured_image_preview=null" class="adm-upload-remove"><X /></button>
              </div>
              <div v-else class="adm-upload" @click="$refs.projImgInput.click()"><Upload /><p>Cliquez pour sélectionner</p></div>
            </div>
            <div class="adm-field">
              <label class="adm-label" style="display:flex;align-items:center;gap:.5rem">
                <div class="adm-toggle" :class="{'adm-toggle--on':projectForm.is_featured}" @click="projectForm.is_featured=!projectForm.is_featured"><span class="adm-toggle__knob"></span></div>
                Mettre en avant
              </label>
            </div>
          </form>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="closeDialog">Annuler</button>
            <button class="adm-btn adm-btn--terra" :disabled="loading" @click="submitForm">
              <span v-if="loading" class="adm-ring adm-ring--sm"></span>
              {{ editingProject?'Modifier':'Créer' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Modal Progression -->
    <Transition name="adm-modal">
      <div v-if="showProgressModal" class="adm-modal" @click.self="showProgressModal=false">
        <div class="adm-modal__box">
          <div class="adm-modal__head"><h3 class="adm-modal__title">Mettre à jour la progression</h3><button class="adm-modal__close" @click="showProgressModal=false"><X /></button></div>
          <div class="adm-modal__body">
            <p style="font-weight:500;margin-bottom:.75rem">{{ progressProject?.title }}</p>
            <div class="adm-field">
              <label class="adm-label">Progression : {{ progressValue }}%</label>
              <input v-model.number="progressValue" type="range" min="0" max="100" class="adm-input" style="padding:.25rem 0" />
              <div class="adm-progress-track" style="margin-top:.5rem"><div class="adm-progress-fill" :style="{width:progressValue+'%'}"></div></div>
            </div>
          </div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="showProgressModal=false">Annuler</button>
            <button class="adm-btn adm-btn--forest" @click="submitProgress"><TrendingUp />Enregistrer</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Delete confirm -->
    <Transition name="adm-modal">
      <div v-if="showDeleteConfirm" class="adm-modal" @click.self="showDeleteConfirm=false">
        <div class="adm-modal__box">
          <div class="adm-modal__head"><h3 class="adm-modal__title">Supprimer le projet ?</h3><button class="adm-modal__close" @click="showDeleteConfirm=false"><X /></button></div>
          <div class="adm-modal__body"><div class="adm-confirm"><div class="adm-confirm__icon"><Trash2 /></div><h3>Confirmer</h3><p>« {{ projectToDelete?.title }} » sera définitivement supprimé.</p></div></div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="showDeleteConfirm=false">Annuler</button>
            <button class="adm-btn adm-btn--red" @click="deleteProjectConfirmed"><Trash2 />Supprimer</button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="adm-toast"><div v-if="toastMessage" class="adm-toast"><CheckCircle />{{ toastMessage }}</div></Transition>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import { Plus, Search, X, ChevronLeft, ChevronRight, MoreVertical, Eye, Edit, Trash2,
  CheckCircle, Users, TrendingUp, DollarSign, Folder, Upload } from 'lucide-vue-next'

export default {
  name: 'AdminProjects',
  components: { Plus, Search, X, ChevronLeft, ChevronRight, MoreVertical, Eye, Edit, Trash2, CheckCircle, Users, TrendingUp, DollarSign, Folder, Upload },
  data() {
    return {
      activeActionMenu:null, showDialog:false, showDeleteConfirm:false, showProgressModal:false,
      editingProject:null, projectToDelete:null, progressProject:null, progressValue:0,
      displayedPages:[], toastMessage:'',
      filters:{search:'',status:'',category:'',sort_by:'created_at',sort_order:'desc'},
      projectForm:{id:null,title:'',slug:'',description:'',full_description:'',category:'',status:'planifié',progress:0,start_date:new Date().toISOString().split('T')[0],end_date:'',location:'',beneficiaries:0,budget:null,currency:'EUR',is_featured:false,featured_image:null,featured_image_preview:null,objectives:[],achievements:[]}
    }
  },
  computed: {
    ...mapState({loading:s=>s.loading,projects:s=>s.projects,projectsPagination:s=>s.projectsPagination,projectStats:s=>s.projectStats,imageUrl:s=>s.imageUrl})
  },
  watch: {
    'projectsPagination.last_page':{handler(){this.updateDisplayedPages()},immediate:true},
    'projectsPagination.current_page':{handler(){this.updateDisplayedPages()}},
    toastMessage:{handler(v){if(v)setTimeout(()=>{this.toastMessage=''},3000)}}
  },
  created(){this.debouncedSearch=debounce(this.applyFilters,500);this.loadData()},
  methods: {
    ...mapActions(['fetchProjects','fetchProjectStats','saveProject','deleteProject','updateProjectProgress','setProjectFilter','setProjectPage']),
    async loadData(){await Promise.all([this.fetchProjects(),this.fetchProjectStats()])},
    openNewProjectDialog(){this.editingProject=null;this.resetProjectForm();this.showDialog=true},
    editProject(p){this.editingProject=p;this.projectForm={id:p.id,title:p.title||'',slug:p.slug||'',description:p.description||'',full_description:p.full_description||'',category:p.category||'',status:p.status||'planifié',progress:p.progress||0,start_date:p.start_date||new Date().toISOString().split('T')[0],end_date:p.end_date||'',location:p.location||'',beneficiaries:p.beneficiaries||0,budget:p.budget||null,currency:p.currency||'EUR',is_featured:p.is_featured||false,featured_image:p.featured_image||null,featured_image_preview:null,objectives:p.objectives||[],achievements:p.achievements||[]};this.showDialog=true;this.activeActionMenu=null},
    closeDialog(){this.showDialog=false;this.editingProject=null;this.resetProjectForm()},
    resetProjectForm(){this.projectForm={id:null,title:'',slug:'',description:'',full_description:'',category:'',status:'planifié',progress:0,start_date:new Date().toISOString().split('T')[0],end_date:'',location:'',beneficiaries:0,budget:null,currency:'EUR',is_featured:false,featured_image:null,featured_image_preview:null,objectives:[],achievements:[]}},
    handleImageUpload(e){const f=e.target.files[0];if(f){this.projectForm.featured_image=f;this.projectForm.featured_image_preview=URL.createObjectURL(f)}},
    async submitForm(){try{const fd=new FormData();if(this.projectForm.id)fd.append('id',this.projectForm.id);['title','description','full_description','category','status','location'].forEach(k=>{if(this.projectForm[k])fd.append(k,this.projectForm[k])});['progress','beneficiaries'].forEach(k=>fd.append(k,this.projectForm[k]));if(this.projectForm.budget)fd.append('budget',this.projectForm.budget);if(this.projectForm.start_date)fd.append('start_date',this.projectForm.start_date);if(this.projectForm.end_date)fd.append('end_date',this.projectForm.end_date);fd.append('is_featured',this.projectForm.is_featured?1:0);if(this.projectForm.featured_image instanceof File)fd.append('featured_image',this.projectForm.featured_image);fd.append('objectives',JSON.stringify(this.projectForm.objectives));fd.append('achievements',JSON.stringify(this.projectForm.achievements));await this.saveProject(fd);this.closeDialog();this.loadData();this.showToast(this.editingProject?'Projet modifié':'Projet créé')}catch(e){console.error(e)}},
    confirmDelete(p){this.projectToDelete=p;this.showDeleteConfirm=true;this.activeActionMenu=null},
    async deleteProjectConfirmed(){if(this.projectToDelete){try{await this.deleteProject(this.projectToDelete.id);this.showDeleteConfirm=false;this.projectToDelete=null;this.loadData();this.showToast('Projet supprimé')}catch(e){console.error(e)}}},
    openProgressModal(p){this.progressProject=p;this.progressValue=p.progress||0;this.showProgressModal=true;this.activeActionMenu=null},
    async submitProgress(){if(this.progressProject){try{await this.updateProjectProgress({id:this.progressProject.id,progress:this.progressValue});this.showProgressModal=false;this.loadData();this.showToast('Progression mise à jour')}catch(e){console.error(e)}}},
    applyFilters(){this.setProjectFilter({key:'search',value:this.filters.search});this.setProjectFilter({key:'status',value:this.filters.status});this.setProjectFilter({key:'category',value:this.filters.category});this.fetchProjects()},
    resetFilters(){this.filters={search:'',status:'',category:'',sort_by:'created_at',sort_order:'desc'};this.applyFilters()},
    changePage(p){if(p>=1&&p<=this.projectsPagination.last_page)this.setProjectPage(p)},
    updateDisplayedPages(){const c=this.projectsPagination.current_page,l=this.projectsPagination.last_page,d=2,pages=[];for(let i=1;i<=l;i++){if(i===1||i===l||(i>=c-d&&i<=c+d))pages.push(i)}this.displayedPages=pages},
    toggleActionsMenu(id){this.activeActionMenu=this.activeActionMenu===id?null:id},
    formatNumber(n){if(!n)return'0';if(n>=1000)return(n/1000).toFixed(1)+'k';return String(n)},
    formatAmount(a){if(!a)return'—';if(a>=1000)return(a/1000).toFixed(1)+'k €';return a.toLocaleString('fr-FR')+' €'},
    fmtDate(d){if(!d)return'—';return new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'short',year:'numeric'})},
    showToast(m){this.toastMessage=m}
  },
  mounted(){document.addEventListener('click',e=>{if(!e.target.closest('.adm-drop'))this.activeActionMenu=null})}
}
</script>
<style>@import './admin.css';</style>
